import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import {
  GroupManualMembershipSyncBody,
  useGroupManualMembershipSyncMutation,
} from "@/common/api/group-manual-membership/use-group-manual-membership-sync.mutation";
import { useGroupManualMembershipUserSearchQuery } from "@/common/api/group-manual-membership/use-group-manual-membership-user-search.query";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { readUrlStateSlice } from "@/common/helpers/url-state";
import type { TableState } from "@/components/data-table/predefined/_components/base-predefined-table";
import {
  USER_WITH_MEMBERSHIP_TABLE_NAME,
  UserWithMembershipTable,
} from "@/components/data-table/predefined/user-with-membership-table";
import { DataTableHeightMode } from "@/components/data-table/types";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams, useSearchParams } from "wouter";
import { createGroupDetailPath, GroupDetailTab } from "../_helpers/group-detail-path";
import { MembershipTableFooter } from "./_components/membership-table-footer";
import { useGroupMembershipSelection } from "./_hooks/use-group-membership-selection.hook";

export function UserView() {
  const { t } = useTranslation(["ram", "common"]);
  const [, navigate] = useLocation();
  const { groupUuid } = useParams<{ groupUuid: string }>();
  const [searchParams] = useSearchParams();
  const initialTableState = useMemo(
    () => readUrlStateSlice<TableState>(searchParams, USER_WITH_MEMBERSHIP_TABLE_NAME),
    [searchParams],
  );

  const [searchTerm, setSearchTerm] = useState(() => initialTableState?.search ?? "");
  const [page, setPage] = useState(() => initialTableState?.page ?? DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(() => initialTableState?.pageSize ?? DEFAULT_PAGE_SIZE);

  const { data: pageData, isLoading: isLoadingPageData } = useGroupManualMembershipUserSearchQuery({
    options: {
      parameters: { search: searchTerm, page, pageSize },
      body: { group: groupUuid ?? "" },
    },
  });

  const { mutateAsync: syncMemberships, isPending: isSyncingMemberships } = useGroupManualMembershipSyncMutation();

  const {
    currentPageSelectedUsers,
    userUuidsToAdd,
    userUuidsToRemove,
    handleSelectionChange,
    handleClearSelection,
    handleRowClick,
  } = useGroupMembershipSelection(pageData?.data);

  const handlePaginationChange = useCallback(({ page, pageSize }: { page: number; pageSize: number }) => {
    setPage(page);
    setPageSize(pageSize);
  }, []);

  const handleCancel = useCallback(
    () => navigate(createGroupDetailPath(groupUuid, GroupDetailTab.Users)),
    [groupUuid, navigate],
  );

  const handleSave = useCallback(async () => {
    const payload: GroupManualMembershipSyncBody = {
      group: groupUuid ?? "",
      usersToAdd: userUuidsToAdd,
      usersToRemove: userUuidsToRemove,
    };

    await toastMutationStatus({
      mutationFn: () => syncMemberships(payload),
      successMessage: t("ram:ManualMembershipsSuccessfullySynced"),
      errorMessage: t("ram:ErrorSyncingManualMemberships"),
    });

    navigate(createGroupDetailPath(groupUuid, GroupDetailTab.Users));
  }, [syncMemberships, groupUuid, navigate, t, userUuidsToAdd, userUuidsToRemove]);

  const totalSelectedCount = (pageData?.meta?.totalSelected ?? 0) - userUuidsToRemove.length + userUuidsToAdd.length;

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Table}
      title={t("ram:ManageManuallyAddedUsers")}
      subTitle={t("ram:MembersOfThisGroup")}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <UserWithMembershipTable
          className="flex-1"
          isLoading={isLoadingPageData}
          data={pageData?.data ?? []}
          autoFetch={false}
          selection={currentPageSelectedUsers}
          selectionMode="object"
          onSelectionChange={handleSelectionChange}
          onClearSelection={handleClearSelection}
          onRowClick={handleRowClick}
          onSearchChange={setSearchTerm}
          page={page}
          pageSize={pageSize}
          onPaginationChange={handlePaginationChange}
          totalItems={pageData?.meta?.total}
          showClearSelection={false}
          heightMode={DataTableHeightMode.Content}
          footerComponent={
            <MembershipTableFooter
              totalSelectedCount={totalSelectedCount}
              isSaving={isSyncingMemberships}
              isSaveDisabled={userUuidsToAdd.length === 0 && userUuidsToRemove.length === 0}
              onClearSelection={handleClearSelection}
              onCancel={handleCancel}
              onSave={handleSave}
            />
          }
        />
      </div>
    </CenteredLayout>
  );
}
